import { useEffect, useRef, useState } from 'react';
import { apiClient } from '@/lib/api';
import type { ResolvedVariables } from './types';

interface UseCrmResolverResult {
  resolved: ResolvedVariables | null;
  resolving: boolean;
}

export function useCrmResolver(phone: string, enabled: boolean): UseCrmResolverResult {
  const [resolved, setResolved] = useState<ResolvedVariables | null>(null);
  const [resolving, setResolving] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    if (!enabled || !phone || phone.length < 8) {
      setResolved(null);
      setResolving(false);
      return;
    }

    let cancelled = false;
    setResolving(true);
    debounceRef.current = setTimeout(() => {
      apiClient
        .get<ResolvedVariables>(`/messaging/resolve-variables?phone=${encodeURIComponent(phone)}`)
        .then((res) => {
          if (cancelled) return;
          setResolved(res.success && res.data ? res.data : null);
        })
        .catch(() => { if (!cancelled) setResolved(null); })
        .finally(() => { if (!cancelled) setResolving(false); });
    }, 500);

    return () => {
      cancelled = true;
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
    };
  }, [phone, enabled]);

  return { resolved, resolving };
}
